/** Wait until a <video> element has frame dimensions (mobile Safari often delays this). */
export function waitForVideoReady(video, timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (!video) {
      resolve(false);
      return;
    }
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      resolve(true);
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(video.videoWidth > 0 && video.videoHeight > 0);
    };
    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", finish);
      video.removeEventListener("loadeddata", finish);
      video.removeEventListener("playing", finish);
      video.removeEventListener("canplay", finish);
    };
    video.addEventListener("loadedmetadata", finish);
    video.addEventListener("loadeddata", finish);
    video.addEventListener("playing", finish);
    video.addEventListener("canplay", finish);
    const timer = setTimeout(finish, timeoutMs);
  });
}

export const CAMERA_START_TIMEOUT_MS = 20000;

export function isMobileCameraDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function formatCameraError(err, { liveCaptureOnly = false } = {}) {
  const fallback = liveCaptureOnly
    ? "Could not open camera — use the phone QR link instead."
    : "Could not open camera — try again.";
  if (!err) return fallback;
  if (err.name === "NotAllowedError") {
    return liveCaptureOnly
      ? "Camera permission denied. Allow access in your browser settings, or use the phone QR link."
      : "Camera permission denied. Allow access in your browser settings.";
  }
  if (err.name === "NotFoundError") {
    return liveCaptureOnly
      ? "No camera found on this device. Scan the QR code with your phone instead."
      : "No camera found on this device.";
  }
  if (err.name === "AbortError") {
    return "Camera start cancelled.";
  }
  return err.message || fallback;
}

export function getUserMediaWithTimeout(constraints, { timeoutMs = CAMERA_START_TIMEOUT_MS, signal } = {}) {
  if (!navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new Error("Camera not supported"));
  }
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };
    const onAbort = () => finish(reject, new DOMException("Aborted", "AbortError"));
    const timer = setTimeout(
      () => finish(reject, new Error("Camera took too long to start. Try upload instead or check browser permissions.")),
      timeoutMs,
    );
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    };
    signal?.addEventListener("abort", onAbort, { once: true });

    navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        if (signal?.aborted) {
          stream.getTracks().forEach((t) => t.stop());
          finish(reject, new DOMException("Aborted", "AbortError"));
          return;
        }
        finish(resolve, stream);
      })
      .catch((err) => finish(reject, err));
  });
}

/** Request rear camera with mobile-friendly fallbacks and a startup timeout. */
export async function requestCameraStream({ facingMode = "environment", signal } = {}) {
  const mobile = isMobileCameraDevice();
  const attempts = mobile
    ? [
        { video: { facingMode: { ideal: facingMode } }, audio: false },
        { video: { facingMode: "environment" }, audio: false },
        { video: { facingMode: "user" }, audio: false },
        { video: true, audio: false },
      ]
    : [
        { video: { facingMode: { ideal: facingMode }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
        { video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
        { video: true, audio: false },
      ];

  let lastErr;
  for (const constraints of attempts) {
    try {
      return await getUserMediaWithTimeout(constraints, { signal });
    } catch (err) {
      lastErr = err;
      if (err?.name === "NotAllowedError" || err?.name === "AbortError") throw err;
    }
  }
  throw lastErr || new Error("Could not open camera");
}

/** Attach a MediaStream to a video element and wait for the first frame. */
export async function bindStreamToVideo(video, stream) {
  if (!video || !stream) return false;
  video.srcObject = stream;
  try {
    await video.play();
  } catch (_) {
    /* iOS may defer play(); loadedmetadata/playing still fire once frames arrive */
  }
  const timeoutMs = isMobileCameraDevice() ? 12000 : 8000;
  return waitForVideoReady(video, timeoutMs);
}
